import { useParams, Link, useNavigate } from "react-router-dom";
import { 
  Star, Clock, Users, Calendar, MapPin, ChevronRight, 
  Monitor, BookOpen, ArrowLeft, CheckCircle, Globe, Play
} from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { sampleOfferings, sampleTeachers } from "@/lib/data";

const offeringTypeLabels: Record<string, string> = {
  "ad-hoc": "Ad-hoc Class",
  "group-class": "Group Class",
  "course": "Course",
  "workshop": "Workshop",
  "tutoring": "Tutoring",
};

export default function OfferingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const offering = sampleOfferings.find(o => o.id === id);
  const teacher = offering ? sampleTeachers.find(t => t.id === offering.teacherId) : null;

  if (!offering || !teacher) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-28">
          <div className="container mx-auto px-4 py-16 text-center">
            <h1 className="font-display text-2xl text-foreground mb-2">Offering not found</h1>
            <p className="text-muted-foreground text-sm mb-6">The offering you're looking for doesn't exist.</p>
            <Button variant="outline" onClick={() => navigate(-1)}>Go Back</Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const spotsLeft = offering.maxStudents - offering.enrolled;
  const fillPercent = Math.round((offering.enrolled / offering.maxStudents) * 100);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-28">
        <div className="container mx-auto px-4 py-8 max-w-5xl">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-muted-foreground text-xs mb-6">
            <Link to="/" className="hover:text-primary transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={`/teacher/${teacher.id}`} className="hover:text-primary transition-colors">{teacher.name}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-foreground font-medium line-clamp-1">{offering.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* ─── Left Content ─── */}
            <div className="lg:col-span-3">
              {/* Hero image */}
              <div className="relative rounded-xl overflow-hidden mb-6">
                <img
                  src={offering.image}
                  alt={offering.title}
                  className="w-full aspect-[16/9] object-cover"
                />
                <div className="absolute top-3 left-3 flex gap-2">
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-background/90 text-foreground backdrop-blur-sm">
                    {offeringTypeLabels[offering.type]}
                  </span>
                </div>
              </div>

              {/* Title & meta */}
              <h1 className="font-display text-2xl sm:text-3xl text-foreground mb-2">{offering.title}</h1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mb-6">
                <span className="flex items-center gap-1">
                  <BookOpen className="w-3.5 h-3.5 text-primary" /> {offering.subject}
                </span>
                <span className="w-px h-4 bg-border" />
                <span className="flex items-center gap-1">
                  <Star className="w-3.5 h-3.5 text-warning fill-warning" />
                  <span className="font-semibold text-foreground">{offering.rating}</span>
                  ({offering.reviewCount} reviews)
                </span>
              </div>

              {/* Description */}
              <div className="mb-8">
                <h2 className="font-display text-lg text-foreground mb-2">About this offering</h2>
                <p className="text-sm text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: offering.description }} />
              </div>

              {/* Details grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                <div className="rounded-xl bg-muted/50 p-4 text-center">
                  <Clock className="w-5 h-5 text-primary mx-auto mb-1.5" />
                  <p className="text-xs text-muted-foreground">Duration</p>
                  <p className="text-sm font-semibold text-foreground">{offering.duration}</p>
                </div>
                <div className="rounded-xl bg-muted/50 p-4 text-center">
                  <Users className="w-5 h-5 text-primary mx-auto mb-1.5" />
                  <p className="text-xs text-muted-foreground">Capacity</p>
                  <p className="text-sm font-semibold text-foreground">{offering.enrolled}/{offering.maxStudents}</p>
                </div>
                {offering.schedule && (
                  <div className="rounded-xl bg-muted/50 p-4 text-center">
                    <Calendar className="w-5 h-5 text-primary mx-auto mb-1.5" />
                    <p className="text-xs text-muted-foreground">Schedule</p>
                    <p className="text-sm font-semibold text-foreground">{offering.schedule}</p>
                  </div>
                )}
              </div>

              {/* Enrollment bar */}
              {offering.maxStudents > 1 && (
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Enrollment</span>
                    <span className="text-sm font-semibold text-foreground">
                      {spotsLeft > 0 ? `${spotsLeft} spots left` : "Full"}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div 
                      className="h-full rounded-full bg-primary transition-all duration-500"
                      style={{ width: `${fillPercent}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* ─── Right Sidebar ─── */}
            <div className="lg:col-span-2">
              <div className="lg:sticky lg:top-24 space-y-5">
                {/* Price card */}
                <div className="rounded-xl border border-border p-5">
                  <div className="mb-4">
                    <span className="font-display text-3xl text-foreground">${offering.price}</span>
                    <span className="text-muted-foreground text-sm ml-1">{offering.priceUnit}</span>
                  </div>
                  {offering.startDate && (
                    <p className="text-xs text-muted-foreground mb-4 flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      Starts {new Date(offering.startDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </p>
                  )}
                  <Button className="w-full mb-2" size="lg">
                    Enroll Now
                  </Button>
                  <Button variant="outline" className="w-full" size="lg">
                    Send Inquiry
                  </Button>
                </div>

                {/* Teacher card */}
                <Link 
                  to={`/teacher/${teacher.id}`}
                  className="flex items-center gap-3 rounded-xl border border-border p-4 hover:border-primary/30 transition-colors"
                >
                  <img
                    src={teacher.avatar}
                    alt={teacher.name}
                    className="w-12 h-12 rounded-xl object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <h3 className="font-semibold text-sm text-foreground truncate">{teacher.name}</h3>
                      {teacher.verified && <CheckCircle className="w-3.5 h-3.5 text-success flex-shrink-0" />}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      <span className="flex items-center gap-1">
                        <Star className="w-3 h-3 text-warning fill-warning" /> {teacher.rating}
                      </span>
                      <span>·</span>
                      <span>{teacher.experience}+ yrs</span>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                </Link>

                {/* Quick facts */}
                <div className="text-xs text-muted-foreground space-y-2 px-1">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-3.5 h-3.5 text-success" />
                    <span>Money-back guarantee</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5 text-primary" />
                    <span>Teacher responds {teacher.responseTime?.toLowerCase()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-3.5 h-3.5" />
                    <span>{offering.enrolled} students enrolled</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
